import classnames from "classnames"
import { Icon } from "../Icon"
import { TimelineMarkerIconVariation, TimelineMarkerProps } from "./types"

/**
 * Visual cue for the milestone
 *   Default: A simple dot
 *   Alternatives: A custom icon with a set of color variations
 *                 A fully custom component for complete flexibility
 *
 * @component
 * @example
 * <Timeline.Marker />
 *
 * <Timeline.Marker icon="CheckCircle" variation="success" />
 *
 * <Timeline.Marker>
 *   <Avatar size="sm" name="Ricardo Moura" />
 * </Timeline.Header>
 */
export const TimelineMarker = ({
  className,
  icon,
  variation = "primary",
  children,
  ...rest
}: TimelineMarkerProps) => {
  return (
    <aside
      className={classnames(
        "absolute -left-4 flex size-8 -translate-y-1 items-center justify-center overflow-hidden rounded-full shadow-none ring-4 ring-white",
        icon && iconVariationClassNameMapping[variation],
        { "bg-white": !icon, "!ring-0": !icon && !children },
        className,
      )}
      {...rest}
    >
      {children || <Icon name={icon || "Dot"} size={icon ? "sm" : "xs"} />}
    </aside>
  )
}

export const iconVariationClassNameMapping: Record<TimelineMarkerIconVariation, string> = {
  error: "text-error-base bg-error-lighten-100",
  gray: "text-black-base bg-gray-200",
  info: "text-info-base bg-info-lighten-100",
  primary: "border border-primary-lighten-50 bg-primary-lighten-100",
  success: "text-success-base bg-success-lighten-100",
  warning: "text-warning-base bg-warning-lighten-100",
}

TimelineMarker.displayName = "TimelineMarker"
